// SdsResultCard.js
// One catalog hit: View SDS / Download / Attach to DEL / Details.
// View and Download are plain anchors (not window.open after an await) so iPad Safari never blocks the tab.

import React, { useState } from "react";
import { Button } from "../ui";
import { getSdsItem, buildSdsDownloadUrl, sdsErrorMessage } from "../../../api/SdsApi";
import { SdsDetailPanel } from "./SdsDetailPanel";
import styles from "./SdsLookupSection.module.css";

export const SdsResultCard = ({ hit, onAttach, isAttached = false, showNotification }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");

  const locale = [hit.country, hit.language].filter(Boolean).join(" / ");
  // The DEL service requires both on add, so a hit missing either can never be attached
  const canAttach = !!hit.product_uid && !!hit.document_link;

  // Fetch the full item once; later toggles only show/hide the cached panel
  const handleToggleDetails = async () => {
    const opening = !detailOpen;
    setDetailOpen(opening);
    if (!opening || detailStatus === "loaded" || detailStatus === "loading") return;
    setDetailStatus("loading");
    try {
      const data = await getSdsItem(hit.product_uid);
      setDetail(data || null);
      setDetailStatus("loaded");
    } catch (err) {
      setDetailStatus("error");
      showNotification?.({
        type: "error",
        title: "SDS details unavailable",
        message: sdsErrorMessage(err),
      });
    }
  };

  return (
    <div className={styles.resultCard} data-testid="sds-result-card">
      <div className={styles.cardTitle}>{hit.product_name || "Unnamed product"}</div>
      <div className={styles.cardMeta}>
        {hit.manufacturer_name && <span>{hit.manufacturer_name}</span>}
        {hit.manufacturer_part_number && <span>Part {hit.manufacturer_part_number}</span>}
        {hit.revision_date && <span>SDS revised {hit.revision_date}</span>}
        {locale && <span>{locale}</span>}
      </div>

      <div className={styles.cardActions}>
        {hit.document_link && (
          <>
            <a
              className={styles.primaryLink}
              href={hit.document_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              View SDS
            </a>
            <a
              className={styles.secondaryLink}
              href={buildSdsDownloadUrl(hit)}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </a>
          </>
        )}
        {onAttach &&
          (isAttached ? (
            <span className={styles.attachedText}>Attached</span>
          ) : (
            <Button
              type="button"
              onClick={() => onAttach(hit)}
              disabled={!canAttach}
              title={canAttach ? undefined : "No SDS link available"}
            >
              Attach to DEL
            </Button>
          ))}
        {/* Library files carry no hazard data, so there is nothing behind Details */}
        {hit.product_uid && hit.source !== "library" && (
          <button
            type="button"
            className={styles.linkButton}
            aria-expanded={detailOpen}
            onClick={handleToggleDetails}
          >
            {detailOpen ? "Hide details" : "Details"}
          </button>
        )}
      </div>

      {detailOpen && detailStatus === "loading" && (
        <div className={styles.detailNote}>Loading details...</div>
      )}
      {detailOpen && detailStatus === "error" && (
        <div className={styles.detailNote}>Details could not be loaded.</div>
      )}
      {detailOpen && detailStatus === "loaded" && <SdsDetailPanel detail={detail} />}
    </div>
  );
};
